/**
 * DynamicAttributeForm — renders a category's resolved form-schema as an
 * editable, RHF-wired attribute form (PIM Wave P2, Task 4).
 *
 * Owns its OWN `useForm` instance whose resolver is the runtime Zod schema
 * built from `schema` (`buildAttributesSchema`). Because a category change
 * swaps the whole schema (and thus the resolver), the PARENT remounts this
 * component via `key={primaryCategoryId}` (Task 9) — a fresh form per category,
 * no resolver-swap gymnastics.
 *
 * Sections render in the schema's group order (ungrouped bucket last, no
 * header); fields render in order; only VISIBLE fields (per `visibility.ts`)
 * are shown. A field that becomes hidden has its value CLEARED — so it is
 * excluded from validation (empty = absent → the Zod builder skips it) and
 * from the payload, and its dependents cascade to hidden too, mirroring the
 * backend's "a hidden field stores no row" law. (Trade-off: re-revealing a
 * previously-filled field starts it empty — matches the backend's inert-hidden
 * semantics; flagged in the build log.)
 *
 * The parent drives submit through the imperative handle:
 *   - `validate()`    — RHF-validate the visible fields, surfacing inline
 *                       errors; returns whether they pass (client mirror of
 *                       `pim.attribute_invalid`; the server 422 stays authority).
 *   - `getValues()`   — the authoritative flat values snapshot at submit.
 *   - `setServerErrors(fields)` — inject the 422 `fields[]` codes onto the
 *                       matching controls (unit codes route to the size unit
 *                       control).
 *   - `focusField(slug)` — used by the completeness meter's jump-links.
 * Live values are also lifted via `onChange` so the header completeness meter
 * re-renders as the seller types.
 */
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  type ReactElement,
  type Ref,
} from "react";
import { Controller, useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  BOOL_UNANSWERED,
  buildAttributesSchema,
  flattenFields,
  isPresent,
  pickText,
  pimCodeToMessageKey,
  unitKey,
  type AttributeFormValues,
  type FormField,
  type FormSchemaResponse,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  buildControllerTypeMap,
  hiddenFieldCount,
  isFieldVisible,
} from "@/features/seller-dashboard/attributes/visibility";
import { TextField } from "@/features/seller-dashboard/attributes/fields/TextField";
import { NumberField } from "@/features/seller-dashboard/attributes/fields/NumberField";
import { BooleanField } from "@/features/seller-dashboard/attributes/fields/BooleanField";
import { SelectField } from "@/features/seller-dashboard/attributes/fields/SelectField";
import { SizeField } from "@/features/seller-dashboard/attributes/fields/SizeField";
import { DateField } from "@/features/seller-dashboard/attributes/fields/DateField";
import { ImageField } from "@/features/seller-dashboard/attributes/fields/ImageField";

/** Imperative surface the editor drives on submit. */
export interface DynamicAttributeFormHandle {
  /** RHF-validate the visible fields; returns true when they all pass. */
  validate: () => Promise<boolean>;
  /** The authoritative current flat values (for the write payload). */
  getValues: () => AttributeFormValues;
  /** Inject 422 `fields[]` codes onto the matching controls. */
  setServerErrors: (fields: ReadonlyArray<{ field_slug: string; code: string }>) => void;
  /** Move focus to a field (completeness jump-links). */
  focusField: (fieldSlug: string) => void;
}

export interface DynamicAttributeFormProps {
  schema: FormSchemaResponse;
  initialValues: AttributeFormValues;
  language: string;
  /** Live values, lifted so the header completeness meter updates as they type. */
  onChange?: (values: AttributeFormValues) => void;
  disabled?: boolean;
}

/** Server `pim.unit_*` codes point at a size field's UNIT control, not its
 * number control. */
const UNIT_CODES = new Set(["pim.unit_missing", "pim.unit_unknown", "pim.unit_not_allowed"]);

function DynamicAttributeFormInner(
  { schema, initialValues, language, onChange, disabled }: DynamicAttributeFormProps,
  ref: Ref<DynamicAttributeFormHandle>,
): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);

  const zodSchema = useMemo(() => buildAttributesSchema(schema), [schema]);
  const controllerTypeBySlug = useMemo(() => buildControllerTypeMap(schema), [schema]);

  const {
    control,
    watch,
    trigger,
    setValue,
    setError,
    getValues,
    formState: { errors },
  } = useForm<AttributeFormValues>({
    // The runtime schema's inferred output is a partial record; the form's
    // values are always fully populated (every field seeded in
    // `initialFormValues`), so the resolver is cast to the concrete shape.
    resolver: zodResolver(zodSchema) as Resolver<AttributeFormValues>,
    defaultValues: initialValues,
    mode: "onChange",
  });

  const values = watch();

  // Lift live values so the header completeness meter re-renders as they type.
  // `values` is a fresh object each render (RHF `watch`) — the intended
  // "on every change" signal; `onChange` is a stable useCallback in the parent.
  useEffect(() => {
    onChange?.(values);
  }, [values, onChange]);

  // Clear hidden fields so they are excluded from validation + payload and the
  // visibility cascade converges (monotone: only ever clears → reaches a fixpoint).
  useEffect(() => {
    for (const field of flattenFields(schema)) {
      if (isFieldVisible(field, controllerTypeBySlug, values)) continue;
      const cur = values[field.field_slug];
      if (field.field_type === "boolean") {
        if (cur !== undefined && cur !== BOOL_UNANSWERED) {
          setValue(field.field_slug, BOOL_UNANSWERED, { shouldDirty: false });
        }
      } else {
        if (isPresent(cur)) {
          setValue(field.field_slug, "", { shouldDirty: false });
        }
        if (field.field_type === "size" && isPresent(values[unitKey(field.field_slug)])) {
          setValue(unitKey(field.field_slug), "", { shouldDirty: false });
        }
      }
    }
  }, [values, schema, controllerTypeBySlug, setValue]);

  const visibleNames = useMemo(() => {
    const names: string[] = [];
    for (const field of flattenFields(schema)) {
      if (!isFieldVisible(field, controllerTypeBySlug, values)) continue;
      names.push(field.field_slug);
      if (field.field_type === "size") names.push(unitKey(field.field_slug));
    }
    return names;
  }, [schema, controllerTypeBySlug, values]);

  useImperativeHandle(
    ref,
    () => ({
      validate: () =>
        visibleNames.length > 0 ? trigger(visibleNames) : Promise.resolve(true),
      getValues: () => getValues(),
      setServerErrors: (fields) => {
        for (const f of fields) {
          const target = UNIT_CODES.has(f.code) ? unitKey(f.field_slug) : f.field_slug;
          setError(target, { type: "server", message: pimCodeToMessageKey(f.code) });
        }
      },
      // DOM-based (not RHF `setFocus`, which needs the field's ref wired — the
      // controlled widgets don't attach one): scroll the field row into view
      // and focus its first focusable control, across all field types.
      focusField: (fieldSlug) => {
        if (typeof document === "undefined") return;
        const row = document.querySelector<HTMLElement>(
          `[data-testid="attr-field-${fieldSlug}"]`,
        );
        if (!row) return;
        row.scrollIntoView({ behavior: "smooth", block: "center" });
        const focusable = row.querySelector<HTMLElement>(
          "input, select, textarea, button, [tabindex]",
        );
        focusable?.focus();
      },
    }),
    [visibleNames, trigger, getValues, setError],
  );

  const renderField = (field: FormField): ReactElement => {
    const err = errors[field.field_slug]?.message as string | undefined;
    const common = { field, language, disabled };
    switch (field.field_type) {
      case "boolean":
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: f }) => (
              <BooleanField {...common} value={f.value} onChange={f.onChange} error={err} />
            )}
          />
        );
      case "select":
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: f }) => (
              <SelectField
                {...common}
                value={f.value}
                onChange={f.onChange}
                onBlur={f.onBlur}
                error={err}
              />
            )}
          />
        );
      case "size":
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: fv }) => (
              <Controller
                control={control}
                name={unitKey(field.field_slug)}
                render={({ field: fu }) => (
                  <SizeField
                    {...common}
                    value={fv.value}
                    onChange={fv.onChange}
                    onBlur={fv.onBlur}
                    error={err}
                    unit={fu.value}
                    onUnitChange={fu.onChange}
                    onUnitBlur={fu.onBlur}
                    unitError={errors[unitKey(field.field_slug)]?.message as string | undefined}
                  />
                )}
              />
            )}
          />
        );
      case "date":
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: f }) => (
              <DateField
                {...common}
                value={f.value}
                onChange={f.onChange}
                onBlur={f.onBlur}
                error={err}
              />
            )}
          />
        );
      case "image":
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: f }) => (
              <ImageField {...common} value={f.value} onChange={f.onChange} error={err} />
            )}
          />
        );
      case "number":
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: f }) => (
              <NumberField
                {...common}
                value={f.value}
                onChange={f.onChange}
                onBlur={f.onBlur}
                error={err}
              />
            )}
          />
        );
      case "text":
      default:
        return (
          <Controller
            key={field.field_slug}
            control={control}
            name={field.field_slug}
            render={({ field: f }) => (
              <TextField
                {...common}
                value={f.value}
                onChange={f.onChange}
                onBlur={f.onBlur}
                error={err}
              />
            )}
          />
        );
    }
  };

  const hidden = hiddenFieldCount(schema, values);

  return (
    <div className="flex flex-col gap-5" data-testid="dynamic-attribute-form">
      {hidden > 0 ? (
        <p role="status" aria-live="polite" className="text-xs text-muted-foreground">
          {t("attributes.hiddenCount", { count: hidden })}
        </p>
      ) : null}
      {schema.groups.map((section) => {
        const visibleFields = section.fields.filter((f) =>
          isFieldVisible(f, controllerTypeBySlug, values),
        );
        if (visibleFields.length === 0) return null;
        return (
          <div
            key={section.group?.id ?? "ungrouped"}
            className="flex flex-col gap-3"
            data-testid={`attr-group-${section.group?.slug ?? "ungrouped"}`}
          >
            {section.group ? (
              <h3 className="text-sm font-semibold text-foreground">
                {pickText(section.group.name, language)}
              </h3>
            ) : null}
            {visibleFields.map((field) => renderField(field))}
          </div>
        );
      })}
    </div>
  );
}

export const DynamicAttributeForm = forwardRef(DynamicAttributeFormInner);
export default DynamicAttributeForm;
